// Route: /billing/refund-status/$refundId — статус запроса на возврат.
// Spec: docs/ui-spec/billing.refund-status.{desktop,mobile}.md

import { Badge } from "@studyhub/ui/components/badge";
import { Button } from "@studyhub/ui/components/button";
import {
	IconAlertOctagon,
	IconArrowLeft,
	IconCircleCheckFilled,
	IconClock,
	IconMessageCircle,
	IconReceipt,
} from "@tabler/icons-react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { isCommerceVisible } from "@/lib/module-availability/commerce";
import { api } from "@/lib/trpc-client";

type RefundStatus = "pending" | "auto_approved" | "approved" | "rejected" | "processed";

export const Route = createFileRoute("/billing/refund-status/$refundId")({
	// Возврат — часть продажи. Пока коммерция закрыта (в мобильном приложении —
	// всегда, App Store 3.1.1), страница не открывается даже по ссылке из письма:
	// это был последний открытый роут биллинга.
	beforeLoad: () => {
		if (!isCommerceVisible()) throw redirect({ to: "/" });
	},
	component: RefundStatusPage,
});

function RefundStatusPage() {
	const { t } = useTranslation("billing");
	const { refundId } = Route.useParams();

	// Реальный tRPC billing.refund.get (row-level: только свой refund).
	const refundQuery = api.billing.refund.get.useQuery(
		{ refundId },
		{ staleTime: 15_000, retry: false },
	);

	if (refundQuery.isLoading) {
		return (
			<div className="flex min-h-dvh items-center justify-center bg-muted/30 px-4">
				<div className="h-40 w-full max-w-2xl animate-pulse rounded-xl bg-muted/40" />
			</div>
		);
	}

	if (refundQuery.isError || !refundQuery.data) {
		return (
			<div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-muted/30 px-4">
				<p className="text-sm text-muted-foreground">{t("refund.notFound")}</p>
				<Button asChild variant="outline" size="sm" className="cursor-pointer">
					<Link to="/settings/billing">
						<IconArrowLeft size={16} stroke={1.75} />
						{t("refund.backCta")}
					</Link>
				</Button>
			</div>
		);
	}

	const data = refundQuery.data;
	// Контракт отдаёт 'pending_review'; UI-компоненты оперируют 'pending'.
	const refund = {
		id: data.id,
		status: (data.status === "pending_review" ? "pending" : data.status) as RefundStatus,
		submittedAt: data.createdAt,
		reason: data.reason,
		amountMinor: data.amountMinor,
		currency: data.currency,
	};

	return (
		<div className="min-h-dvh bg-muted/30">
			<header className="sticky top-0 z-10 flex h-[calc(3.5rem+env(safe-area-inset-top,0px))] items-center justify-between border-b border-border bg-background/95 px-4 pt-[env(safe-area-inset-top,0px)] backdrop-blur sm:px-6">
				<Link
					to="/"
					className="cursor-pointer text-base font-semibold tracking-tight text-foreground"
				>
					StudyHub
				</Link>
				<Button
					asChild
					variant="ghost"
					size="sm"
					className="cursor-pointer pointer-coarse:min-h-11 pointer-coarse:min-w-11"
				>
					<Link to="/settings/billing">
						<IconArrowLeft size={16} stroke={1.75} />
						<span className="ml-1 hidden sm:inline">{t("refund.backCta")}</span>
					</Link>
				</Button>
			</header>

			<main className="mx-auto w-full max-w-2xl px-4 py-6 sm:py-10">
				<div className="rounded-[var(--radius-panel)] border border-border bg-card p-6 shadow-e2">
					<div className="flex items-start gap-4">
						<StatusIcon status={refund.status} />
						<div className="flex-1">
							<h1 className="text-lg font-semibold text-foreground">{t("refund.statusTitle")}</h1>
							<p className="mt-1 text-xs text-muted-foreground">
								{t("refund.submittedAt", {
									date: new Date(refund.submittedAt).toLocaleString(),
								})}
							</p>
							<div className="mt-3">
								<StatusBadge status={refund.status} />
							</div>
						</div>
					</div>

					<dl className="mt-6 space-y-3 border-t border-border pt-5 text-sm">
						<Row label={t("refund.reasonLabel")} value={t(`refund.reasons.${refund.reason}`)} />
						<Row
							label="ID"
							value={<span className="font-mono text-xs">{refund.id.slice(0, 8)}…</span>}
						/>
					</dl>
				</div>

				<section className="mt-6 rounded-[var(--radius-panel)] border border-border bg-card p-6 shadow-e2">
					<h2 className="mb-4 text-sm font-semibold text-foreground">
						{t("refund.timelineTitle")}
					</h2>
					<Timeline status={refund.status} />
				</section>

				<div className="mt-6 flex flex-col gap-2 sm:flex-row">
					<Button asChild variant="outline" className="cursor-pointer">
						<Link to="/settings/billing">
							<IconReceipt size={16} stroke={1.75} />
							{t("refund.backCta")}
						</Link>
					</Button>
					<Button asChild variant="ghost" className="cursor-pointer">
						<a href="mailto:info@iec.exchange">
							<IconMessageCircle size={16} stroke={1.75} />
							{t("refund.supportCta")}
						</a>
					</Button>
				</div>
			</main>
		</div>
	);
}

function StatusIcon({ status }: { status: RefundStatus }) {
	if (status === "rejected") {
		return (
			<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-destructive/10">
				<IconAlertOctagon size={24} className="text-destructive" />
			</div>
		);
	}
	if (status === "auto_approved" || status === "approved" || status === "processed") {
		return (
			<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-success/10">
				<IconCircleCheckFilled size={28} className="text-success" />
			</div>
		);
	}
	return (
		<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-warning/10">
			<IconClock size={24} className="text-warning" />
		</div>
	);
}

function StatusBadge({ status }: { status: RefundStatus }) {
	const { t } = useTranslation("billing");
	const variant: "secondary" | "default" | "destructive" =
		status === "rejected" ? "destructive" : status === "pending" ? "secondary" : "default";
	return <Badge variant={variant}>{t(`refund.status.${status}`)}</Badge>;
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
	return (
		<div className="flex items-baseline justify-between gap-3">
			<dt className="text-xs text-muted-foreground">{label}</dt>
			<dd className="text-sm font-medium text-foreground">{value}</dd>
		</div>
	);
}

function Timeline({ status }: { status: RefundStatus }) {
	const { t } = useTranslation("billing");
	const steps: Array<{ key: string; label: string; done: boolean }> = [
		{ key: "submitted", label: t("refund.timeline.submitted"), done: true },
		{
			key: "reviewing",
			label: t("refund.timeline.reviewing"),
			done: ["auto_approved", "approved", "rejected", "processed"].includes(status),
		},
		{
			key: "decision",
			label: t("refund.timeline.decision"),
			done: ["auto_approved", "approved", "rejected", "processed"].includes(status),
		},
		{
			key: "money_back",
			label: t("refund.timeline.money_back"),
			done: status === "processed",
		},
	];

	return (
		<ol className="space-y-3">
			{steps.map((s, idx) => (
				<li key={s.key} className="flex items-center gap-3">
					<span
						className={cn(
							"flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium tabular-nums",
							s.done
								? "border-primary bg-primary text-primary-foreground"
								: "border-border bg-background text-muted-foreground",
						)}
					>
						{idx + 1}
					</span>
					<span className={cn("text-sm", s.done ? "text-foreground" : "text-muted-foreground")}>
						{s.label}
					</span>
				</li>
			))}
		</ol>
	);
}
